import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiSettings, FiActivity } = FiIcons;

const CallDashboard = () => {
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [formData, setFormData] = useState({
    fromNumber: '',
    toNumber: '',
    mode: 'sales',
    agentGoal: ''
  });
  const [callStatus, setCallStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTwilioNumbers();
  }, []);

  const fetchTwilioNumbers = async () => {
    try {
      const response = await fetch('/api/twilio/numbers');
      const data = await response.json();
      setTwilioNumbers(data.numbers || []);
    } catch (error) {
      console.error('Error fetching Twilio numbers:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const startOutgoingCall = async () => {
    if (!formData.fromNumber || !formData.toNumber) {
      alert('Please select a Twilio number and enter a recipient number');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/twilio/outgoing-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      if (result.ok) {
        setCallStatus({
          callId: result.callId,
          twilioCallSid: result.twilioCallSid,
          status: 'initiated'
        });
      }
    } catch (error) {
      console.error('Error starting call:', error);
      alert('Failed to start call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6"
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiPhone} className="text-2xl text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">Call Dashboard</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Call Configuration */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center">
              <SafeIcon icon={FiSettings} className="mr-2" />
              Call Configuration
            </h2>

            {/* Twilio Number Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Twilio Number (From)
              </label>
              <select
                value={formData.fromNumber}
                onChange={(e) => handleInputChange('fromNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a number...</option>
                {twilioNumbers.map((number, index) => (
                  <option key={index} value={number.phoneNumber}>
                    {number.friendlyName} ({number.phoneNumber})
                  </option>
                ))}
              </select>
            </div>

            {/* Recipient Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Number (To)
              </label>
              <input
                type="tel"
                value={formData.toNumber}
                onChange={(e) => handleInputChange('toNumber', e.target.value)}
                placeholder="+1234567890"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Agent Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Mode
              </label>
              <select
                value={formData.mode}
                onChange={(e) => handleInputChange('mode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="sales">Sales Agent</option>
                <option value="booking">Booking Agent</option>
                <option value="reception">Incoming Only</option>
              </select>
            </div>

            {/* Agent Goal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Goal / Instructions
              </label>
              <textarea
                value={formData.agentGoal}
                onChange={(e) => handleInputChange('agentGoal', e.target.value)}
                rows={4}
                placeholder="Enter agent instructions and goals..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              />
            </div>

            {/* Start Call Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={startOutgoingCall}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : (
                <SafeIcon icon={FiPhone} className="mr-2" />
              )}
              {loading ? 'Starting Call...' : 'Start Outgoing Call'}
            </motion.button>
          </div>

          {/* Call Status */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center">
              <SafeIcon icon={FiActivity} className="mr-2" />
              Call Status
            </h2>

            <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
              {callStatus ? (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Call ID:</span>
                    <p className="text-gray-800 font-mono text-sm">{callStatus.callId}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Twilio Call SID:</span>
                    <p className="text-gray-800 font-mono text-sm">{callStatus.twilioCallSid}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Status:</span>
                    <p className="text-gray-800 capitalize">{callStatus.status}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>No active calls</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CallDashboard;